import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Clock3, ListChecks, Percent, ScrollText, ShieldCheck, History } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { PageBar } from "@/components/layout/PageBar";
import { Skeleton } from "@/components/common";
import { RatesVersionBar, AuditLog } from "@/components/settings/RatesVersionBar";
import { LaborRatesTable } from "@/components/settings/LaborRatesTable";
import { CategoriesTable } from "@/components/settings/CategoriesTable";
import { PricelistTable } from "@/components/settings/PricelistTable";
import { RulesTable, ApprovalTable } from "@/components/settings/RulesTable";

function Section({ icon: Icon, title, description, children, testId }) {
  return (
    <section id={testId} className="scroll-mt-6 grid grid-cols-1 gap-5 border-t border-black/[0.08] py-8 lg:grid-cols-[240px_minmax(0,1fr)]" data-testid={testId}>
      <div><h2 className="flex items-center gap-2 text-[14.5px] font-semibold"><Icon className="h-4 w-4 text-muted-foreground" strokeWidth={1.9} /> {title}</h2><p className="mt-1 text-[13px] leading-relaxed text-muted-foreground">{description}</p></div>
      <div className="min-w-0">{children}</div>
    </section>
  );
}

const detail = (e) => {
  const d = e?.response?.data?.detail;
  if (d?.errors) return d.errors.map((x) => x.message).join(" · ");
  return typeof d === "string" ? d : errMsg(e);
};

/**
 * Admin: versioned rates and rules behind the calculation. Active/previous versions are frozen; edits live in a draft.
 * Server writes are fail-closed (403) until an explicit admin right exists – the UI still lets you edit and validate locally.
 */
export default function RatesSettingsPage() {
  const [meta, setMeta] = useState(null);
  const [selected, setSelected] = useState(null);
  const [view, setView] = useState(null);
  const [draft, setDraft] = useState(null);
  const [editing, setEditing] = useState(false);
  const [errors, setErrors] = useState([]);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);
  const timer = useRef(null);

  const load = useCallback(async () => {
    const r = await api.get("/los-settings");
    setMeta(r.data);
    const pick = r.data.draft?.version || r.data.active?.version;
    setSelected((s) => s || pick);
    return r.data;
  }, []);
  useEffect(() => { load().catch((e) => toast.error(errMsg(e))); }, [load]);
  useEffect(() => {
    if (!selected) return;
    api.get(`/los-settings/versions/${selected}`).then((r) => { setView(r.data); setDraft(null); setEditing(false); setErrors([]); }).catch((e) => toast.error(errMsg(e)));
  }, [selected]);

  const data = draft || view?.data;
  const dirty = Boolean(draft) && JSON.stringify(draft) !== JSON.stringify(view?.data);
  const validate = useCallback((d) => {
    clearTimeout(timer.current);
    timer.current = setTimeout(() => api.post("/los-settings/validate", { data: d }).then((r) => setErrors(r.data.errors || [])).catch(() => {}), 350);
  }, []);
  const update = (section, value) => {
    const next = { ...(draft || view.data), [section]: value };
    setDraft(next);
    validate(next);
  };

  const startEdit = async () => {
    if (view.status === "utkast") { setDraft(view.data); setEditing(true); return; }
    try {
      const r = await api.post("/los-settings/draft", { note: `Fra v${view.version}` });
      await load();
      setSelected(r.data.version);
      setTimeout(() => setEditing(true), 0);
      toast.success(`Kladd v${r.data.version} opprettet`);
    } catch (e) {
      toast.error(detail(e));
      setDraft(JSON.parse(JSON.stringify(view.data)));
      setEditing(true);
    }
  };
  const save = async () => {
    setSaving(true);
    try {
      const r = await api.patch(`/los-settings/versions/${view.version}`, { patch: draft });
      setView(r.data); setDraft(null); setEditing(false);
      await load();
      toast.success("Kladden er lagret og loggført");
    } catch (e) {
      toast.error(detail(e));
    } finally {
      setSaving(false);
    }
  };
  const activate = async () => {
    if (!window.confirm(`Aktivere v${view.version}? Aktiv versjon fryses. Eksisterende saker beholder sin versjon til de oppdateres.`)) return;
    setSaving(true);
    try {
      await api.post(`/los-settings/versions/${view.version}/activate`);
      await load();
      setSelected(view.version);
      toast.success(`v${view.version} er aktiv`);
    } catch (e) {
      toast.error(detail(e));
    } finally {
      setSaving(false);
    }
  };
  const discard = async () => {
    if (!window.confirm(`Forkaste kladd v${view.version}?`)) return;
    try {
      await api.delete(`/los-settings/versions/${view.version}`);
      const m = await load();
      setSelected(m.active.version);
      toast.success("Kladden er forkastet");
    } catch (e) {
      toast.error(detail(e));
    }
  };
  const importPdf = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post("/los-settings/pricelist/parse", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const next = { ...(draft || view.data), pricelist: { ...view.data.pricelist, valid_from: r.data.valid_from, lines: r.data.lines, general_terms: r.data.general_terms, source: r.data.name, provenance: { status: "bekreftet", source: `${r.data.name} (PDF, lest ${new Date().toISOString().slice(0, 10)})`, valid_from: r.data.valid_from, checked: false, note: "Ny innlesing – tolkning ikke kontrollert" } } };
      setDraft(next); setEditing(true); validate(next);
      toast.success(`${r.data.lines.length} linjer lest fra ${r.data.name} – kontroller og lagre kladden`);
    } catch (e) {
      toast.error(detail(e));
    }
  };

  const assumedDb = useMemo(() => Number(data?.cost_assumptions?.db_pct_for_unknown_cost?.value), [data]);

  return (
    <div className="mx-auto w-full max-w-[1200px] px-5 pb-16 pt-5 sm:px-8" data-testid="rates-settings-page">
      <PageBar title="Kalkyle & satser" meta="Versjonert – aktive versjoner er fryst, endringer blir en ny versjon" back={{ to: "/settings", label: "Innstillinger" }} testId="rates-bar" className="pb-4" />
      {!meta || !view || !data ? <div className="space-y-4"><Skeleton className="h-16" /><Skeleton className="h-64" /></div> : (
        <>
          <RatesVersionBar meta={meta} view={view} editing={editing} dirty={dirty} errors={errors} canWrite={meta.can_write} blockReason={meta.write_block_reason} onStartEdit={startEdit} onCancelEdit={() => { setDraft(null); setEditing(false); setErrors([]); }} onSave={save} onActivate={activate} onDiscard={discard} onSelectVersion={setSelected} saving={saving} />
          <Section icon={Clock3} title="Timesatser" description="Salgspriser fra LOS-prislisten 2026 er bekreftet. 705/970 fra kalkylemalen er historisk. Intern kost er ukjent der LOS ikke har oppgitt den." testId="rates-section-labor">
            <LaborRatesTable rates={data.labor_rates} editing={editing} errors={errors} onChange={(v) => update("labor_rates", v)} assumedDb={Number.isFinite(assumedDb) ? assumedDb : null} />
          </Section>
          <Section icon={Percent} title="Kategorier og antakelser" description="Standard prisingsmetode per kostnadstype – DB % av salgspris eller påslag % på kost er to ulike regnestykker. Demoantakelsen om DB brukes bare til å simulere ukjent kost." testId="rates-section-categories">
            <CategoriesTable categories={data.categories} assumptions={data.cost_assumptions} editing={editing} errors={errors} onChange={(v) => update("categories", v)} onChangeAssumptions={(v) => update("cost_assumptions", v)} />
          </Section>
          <Section icon={ListChecks} title="LOS-prisliste" description="Lest deterministisk fra den leverte prislisten. Enhet og faktureringsmåte følger linjen. Kryss av «kontrollert» når tolkningen er sjekket mot PDF-en." testId="rates-section-pricelist">
            <PricelistTable pricelist={data.pricelist} editing={editing} onChange={(v) => update("pricelist", v)} onImportClick={() => fileRef.current?.click()} />
            <input ref={fileRef} type="file" accept=".pdf" className="hidden" onChange={(e) => { importPdf(e.target.files?.[0]); e.target.value = ""; }} data-testid="rates-pricelist-file" />
          </Section>
          <Section icon={ScrollText} title="Prisregler" description="Minstemengder gjelder bare tjenestene som oppgir dem. Mobilisering fra prislistens generelle vilkår. Avrunding er uavklart." testId="rates-section-rules">
            <RulesTable rules={data.rules} editing={editing} errors={errors} onChange={(v) => update("rules", v)} />
          </Section>
          <Section icon={ShieldCheck} title="Godkjenning" description="Beløpstrinn og hva de krever. Demonstrasjon til LOS' fullmaktsmatrise er bekreftet – gir ingen reell fullmakt." testId="rates-section-approval">
            <ApprovalTable approval={data.approval} editing={editing} errors={errors} onChange={(v) => update("approval", v)} />
          </Section>
          <Section icon={History} title="Versjoner og logg" description="Hvem endret hva, når – og hvilken versjon som er aktiv." testId="rates-section-audit">
            <ul className="mb-3 flex flex-wrap gap-2" data-testid="rates-versions">
              {meta.versions.map((v) => <li key={v.version}><button type="button" onClick={() => setSelected(v.version)} className={`h-7 rounded-full border px-2.5 text-[12px] ${selected === v.version ? "border-foreground bg-foreground text-white" : "border-black/[0.1] bg-white hover:border-black/[0.25]"}`} data-testid={`rates-version-chip-${v.version}`}>v{v.version} · {v.status}</button></li>)}
            </ul>
            <AuditLog entries={meta.audit} />
          </Section>
        </>
      )}
    </div>
  );
}
